import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const TaxProfessionals = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Tax Professionals: Special features and services"
          description="Special features and services IRS tax professionals, CPA, and experts. E-Filing Solution for multiple IRS Tax Forms, Flexible Pricing Options, and more."
          keywords="IRS Tax Professionals, Tax Experts, CPA, Certified Public Accountants, Trucking Tax Service Providers, IRS e-filing for CPAs, tax professionals, software for tax professionals"
          canonical="tax-professionals/"
        />
      }
    >
      <Container maxWidth="xl" component="section">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="header"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                Powerful E-Filing Solution for{' '}
                <span className="highlight-container">
                  <span className="highlight">Tax Professionals</span>
                </span>{' '}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Your search for a seamless tax e-filing experience tailored to
                tax professionals ends here.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                TaxZerone offers a comprehensive suite of features designed to
                streamline your tax preparation and filing process. With our
                platform, you can deliver exceptional service to your clients.
              </Typography>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=taxprofessional`}
                  >
                    Create an Account
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden lgDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
              className="banner-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/tax-professional-img.webp" />
                <source srcSet="/assets/images/tax-professional-img.png" />
                <img
                  src="/assets/images/tax-professional-img.png"
                  alt="Powerful E-Filing Solution for Tax Professionals"
                  width={660}
                  height={544}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden lgUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
              className="banner-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/tax-professional-img-sm.webp" />
                <source srcSet="/assets/images/tax-professional-img-sm.png" />
                <img
                  src="/assets/images/tax-professional-img-sm.png"
                  alt="Powerful E-Filing Solution for Tax Professionals"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={4}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                The only solution you&apos;ll ever need to e-file
                <Hidden mdDown>
                  <br />
                </Hidden>{' '}
                and manage tax returns
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Check out the features offered by TaxZerone for tax
                professionals and serve your clients
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={2}
            mt={4}
            pb={{ xs: 0, sm: 0, md: 0 }}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/multiple-icon.webp" />
                    <source srcSet="/assets/images/multiple-icon.png" />
                    <img
                      src="/assets/images/multiple-icon.png"
                      alt="Multiple IRS Forms Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Multiple IRS Forms Support
                </Typography>
                <Typography variant="body1">
                  Supports a wide range of IRS forms, ensuring that you can
                  effortlessly file tax returns without any hassle. From
                  individual tax returns to business filings, we&apos;ve got you
                  covered.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/flexi-icon.webp" />
                    <source srcSet="/assets/images/flexi-icon.png" />
                    <img
                      src="/assets/images/flexi-icon.png"
                      alt="Flexible Pricing Options"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Flexible Pricing Options
                </Typography>
                <Typography variant="body1">
                  We understand that every tax professional&apos;s needs are
                  unique. That&apos;s why we offer flexible pricing options. We
                  have a pricing structure in place that fits your business.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/dashboard-icon.webp" />
                    <source srcSet="/assets/images/dashboard-icon.png" />
                    <img
                      src="/assets/images/dashboard-icon.png"
                      alt="Dashboard to Manage Multiple Businesses"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Dashboard to Manage Multiple Businesses
                </Typography>
                <Typography variant="body1">
                  Manage multiple businesses with an intuitive dashboard. Easily
                  navigate between clients, track their tax filing status, and
                  access relevant documents without needing multiple logins or
                  complex setups.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/irs-icon.webp" />
                    <source srcSet="/assets/images/irs-icon.png" />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="E-File Center for Efficient Tax Filings"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  E-File Center for Efficient Tax Filings
                </Typography>
                <Typography variant="body1">
                  Centralized location for managing all your tax filings. Manage
                  tax returns filed with the IRS with ease, ensuring accuracy
                  and compliance at every step.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/upload-icon.webp" />
                    <source srcSet="/assets/images/upload-icon.png" />
                    <img
                      src="/assets/images/upload-icon.png"
                      alt="Bulk Upload Options"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Bulk Upload Options
                </Typography>
                <Typography variant="body1">
                  Save time by uploading multiple tax return info in one go.
                  Quickly import and process large batches of tax return data,
                  reduce manual effort significantly, and increase productivity.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/sign-icon.webp" />
                    <source srcSet="/assets/images/sign-icon.png" />
                    <img
                      src="/assets/images/sign-icon.png"
                      alt="Signature Forms for ERO"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Signature Forms for ERO
                </Typography>
                <Typography variant="body1">
                  Simplified process for getting client signatures.
                  Electronically sign tax documents securely and send them to
                  your clients, ensuring a seamless experience for you and your
                  clients.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/support-icon.webp" />
                    <source srcSet="/assets/images/support-icon.png" />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Dedicated Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Dedicated Support
                </Typography>
                <Typography variant="body1">
                  We value your success and provide dedicated support to assist
                  you every step of the way. Our support team is available to
                  answer your questions, resolve issues promptly, and ensure
                  your e-filing experience is smooth and hassle-free.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/mobile-icon.webp" />
                    <source srcSet="/assets/images/mobile-icon.png" />
                    <img
                      src="/assets/images/mobile-icon.png"
                      alt="Mobile Apps"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Mobile Apps
                </Typography>
                <Typography variant="body1">
                  Stay connected and manage your tax filings on the go with our
                  mobile apps. Access essential features and monitor your
                  clients&apos; tax status from mobile or tablet, providing
                  flexibility and convenience wherever you are.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/instant-icon.webp" />
                    <source srcSet="/assets/images/instant-icon.png" />
                    <img
                      src="/assets/images/instant-icon.png"
                      alt="Reminders & Notifications"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Reminders & Notifications
                </Typography>
                <Typography variant="body1">
                  Stay informed of your tax return status and form deadlines.
                  TaxZerone sends timely reminders and notifications to keep you
                  informed about deadlines, the status of your e-filed returns,
                  and more.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={{ xs: -2, md: 5 }}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              textAlign="center"
              mt={{ xs: 0, md: 0 }}
              mb={{ xs: 0, sm: 0, md: 0 }}
            >
              <Button
                variant="contained"
                sx={{
                  mt: 4,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=taxprofessional`}
              >
                Get Started with TaxZerone
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Takes less than a minute
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
        component="section"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Ready to simplify your tax e-filing process and{' '}
          <Hidden mdDown>
            <br />
          </Hidden>{' '}
          elevate your productivity as a tax professional?
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
          }}
        >
          Create an account with TaxZerone and start filing tax returns for your
          clients accurately and securely.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=taxprofessional`}
          >
            Get Started with&nbsp;<b>TaxZerone</b>
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: '#dfeae1' }}>
            Takes less than a minute
          </Typography>
        </Box>
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',

          mx: '0 !important',
          width: '100% !important',
          py: { xs: 1, sm: 3 },
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={2}
            pb={3}
            className="MuiGrid-root-width"
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                The E-filing software that keeps security as a priority
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 62, height: 54 }}>
                    <source srcSet="/assets/images/secure-white-icon.webp" />
                    <source srcSet="/assets/images/secure-white-icon.png" />
                    <img
                      src="/assets/images/secure-white-icon.png"
                      alt="Security and Compliance"
                      loading="lazy"
                      width={62}
                      height={54}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Security and Compliance
                </Typography>
                <Typography variant="body1">
                  TaxZerone adheres to the highest security standards, ensuring
                  that your data remains secure and confidential throughout the
                  entire process.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 62, height: 54 }}>
                    <source srcSet="/assets/images/compliance-white-icon.webp" />
                    <source srcSet="/assets/images/compliance-white-icon.png" />
                    <img
                      src="/assets/images/compliance-white-icon.png"
                      alt="Compliance with Industry Regulations"
                      loading="lazy"
                      width={62}
                      height={54}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Compliance with Industry Regulations
                </Typography>
                <Typography variant="body1">
                  We comply with the industry regulations and guidelines set
                  forth by the IRS. You can rest assured that your tax filings
                  adhere to the latest IRS requirements.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 62, height: 54 }}>
                    <source srcSet="/assets/images/audit-white-icon.webp" />
                    <source srcSet="/assets/images/audit-white-icon.png" />
                    <img
                      src="/assets/images/audit-white-icon.png"
                      alt="Regular Security Audits"
                      loading="lazy"
                      width={62}
                      height={54}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Regular Security Audits
                </Typography>
                <Typography variant="body1">
                  Our dedicated team of security experts continuously monitors
                  the platform, performs security testing, and implements
                  necessary security patches and updates.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" component="section">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              FAQs
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5, border: '2px solid rgba(145, 158, 171, 0.35)' }}
            >
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls=""
                  id=""
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                  >
                    1. Is my data secure when using your tax e-filing product?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Absolutely! We take utmost priority to protect your data. We
                    employ advanced encryption protocols to secure data
                    transmission, utilize secure data centers, and adhere to
                    industry best practices for data storage and retention.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls=""
                  id=""
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                  >
                    2. Can I file various IRS forms using TaxZerone?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Yes! TaxZerone supports a wide range of IRS forms, including
                    excise forms, extension forms, and exempt organization
                    forms. We strive to provide comprehensive support for the
                    forms you need to handle diverse tax scenarios.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls=""
                  id=""
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                  >
                    3. What pricing options are available for tax professionals?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    We offer flexible pricing options to suit your needs. We
                    have pricing structures that cater to different business
                    requirements. Contact our team to discuss the best pricing
                    option for your tax professional needs.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default TaxProfessionals;
